import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  deleteDeliveryService,
  getDeliveryServices,
} from "../../../api/DeliveryService";
import RemoveModal from "../../modals/removeModal";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

// Icons
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

// Utils
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import DeliveryServiceListRow from "./DeliveryServiceListRow";
import DeliveryServiceCreateEdit from "./DeliveryServiceCreateEdit";

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

export default function DeliveryServicesModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [deliveryServices, setDeliveryServices] = useState([]);
  const [serviceToEditId, setServiceToEditId] = useState(null);
  const [showCreateEditServiceModal, setShowCreateEditServiceModal] =
    useState(false);
  const [serviceToRemoveId, setServiceToRemoveId] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [page, setPage] = useState(1);
  const [showButtonMore, setShowButtonMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // For forcing re-render after loading
  const [forceUpdate, setForceUpdate] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setLoading(true);
    loadDeliveryServices(true, null)
      .then(() => {
        setIsFirstLoad(false);
        // Force update to ensure animations work correctly
        setForceUpdate((prev) => prev + 1);
      })
      .catch((error) => {
        setErrorMessage(
          "Ocorreu um erro ao carregar os dados. Tente novamente."
        );
        setShowError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [show]);

  // Filter delivery services based on search term
  const filteredDeliveryServices = useMemo(() => {
    if (!searchTerm) return deliveryServices;

    return deliveryServices.filter((service) => {
      const serviceName = service.name?.toLowerCase() || "";
      const serviceDescription = service.description?.toLowerCase() || "";
      const searchLower = searchTerm.toLowerCase();

      return (
        serviceName.includes(searchLower) ||
        serviceDescription.includes(searchLower)
      );
    });
  }, [deliveryServices, searchTerm]);

  const loadDeliveryServices = async (newSearch, filtersData) => {
    try {
      if (newSearch) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const { data } = await getDeliveryServices();
      data.sort((a, b) => a.name.localeCompare(b.name));

      let result;
      if (newSearch) {
        result = [...data];
        switchShowButtonMore(data.length);
      } else {
        const new_services = data.filter(
          ({ id: id1 }) => !deliveryServices.some(({ id: id2 }) => id2 === id1)
        );
        result = [...deliveryServices, ...new_services];
        switchShowButtonMore(new_services.length);
      }

      setPage(page + 1);
      setDeliveryServices(result);

      // Force update to ensure animations work correctly
      setTimeout(() => {
        setForceUpdate((prev) => prev + 1);
      }, 100);
    } catch (err) {
      console.log(err);
      setErrorMessage("Erro ao carregar serviços de entrega. Tente novamente.");
      setShowError(true);
      dispatchSnackBarMessage(
        "delivery-services-load-error",
        "Erro ao carregar serviços de entrega",
        "error"
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const switchShowButtonMore = (length) => {
    length > 0 ? setShowButtonMore(true) : setShowButtonMore(false);
  };

  const openCreateServiceModal = () => {
    setServiceToEditId(null);
    setShowCreateEditServiceModal(true);
  };

  const closeCreateEditServiceModal = useCallback(() => {
    setShowCreateEditServiceModal(false);
  }, []);

  const editService = useCallback((id) => {
    setServiceToEditId(id);
    setShowCreateEditServiceModal(true);
  }, []);

  const toggleDeleteModal = useCallback(
    (id) => {
      setServiceToRemoveId(id);
      setShowRemoveModal(!showRemoveModal);
    },
    [showRemoveModal]
  );

  const handleDeleteService = async () => {
    try {
      setLoading(true);
      await deleteDeliveryService(serviceToRemoveId);
      await loadDeliveryServices(true);
      setShowRemoveModal(false);
      dispatchSnackBarMessage(
        "service-delete-success",
        "Serviço de entrega removido com sucesso",
        "success"
      );
    } catch (e) {
      console.log(e);
      dispatchSnackBarMessage(
        "service-delete-error",
        "Erro ao remover serviço de entrega",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle search reset
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Handle page refresh
  const handleRefresh = () => {
    loadDeliveryServices(true, null);
  };

  // Directly render delivery service rows without animation variants
  const renderDeliveryServiceRows = () => {
    return filteredDeliveryServices.map((service, index) => (
      <div key={`delivery-service-list-${service.id}`} style={{ opacity: 1 }}>
        <DeliveryServiceListRow
          index={index + 1}
          deliveryService={service}
          editAction={() => editService(service.id)}
          deleteAction={() => toggleDeleteModal(service.id)}
        />
      </div>
    ));
  };

  return (
    <>
      <AnimatedModal
        isOpen={show}
        onClose={closeModal}
        width="80%"
        maxWidth="1200px"
      >
        <ModalHeader
          title="Serviços de Entrega"
          icon={LocalShippingIcon}
          onClose={closeModal}
        />

        <ModalContent>
          <Box sx={{ px: 3, pt: 2, pb: 4 }}>
            <AnimatePresence>
              {showError && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="error"
                    onClose={() => setShowError(false)}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions Bar */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {/* Add Service Button */}
              <motion.div
                whileHover="hover"
                whileTap="tap"
                variants={buttonVariants}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateServiceModal}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    fontWeight: "medium",
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: theme.shadows[2],
                  }}
                >
                  Novo Serviço de Entrega
                </Button>
              </motion.div>

              {/* Search Field */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  flex: { xs: "1 1 100%", sm: "1 1 auto" },
                  maxWidth: { sm: 300 },
                  ml: { sm: "auto" },
                  mr: { sm: 2 },
                }}
              >
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 10,
                    color: theme.palette.text.secondary,
                    pointerEvents: "none",
                  }}
                />
                <Box
                  component="input"
                  placeholder="Buscar serviço..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    borderRadius: 2,
                    py: 1,
                    px: 4,
                    width: "100%",
                    outline: "none",
                    "&:focus": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(
                        theme.palette.primary.main,
                        0.2
                      )}`,
                    },
                    fontSize: "0.875rem",
                  }}
                />

                {searchTerm && (
                  <Tooltip title="Limpar busca">
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      sx={{
                        position: "absolute",
                        right: 10,
                        cursor: "pointer",
                        color: theme.palette.text.secondary,
                      }}
                      onClick={handleClearSearch}
                    >
                      ×
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Refresh Button */}
              <Tooltip title="Atualizar lista">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={loading}
                    sx={{
                      minWidth: 0,
                      p: 1,
                      borderRadius: 2,
                      aspectRatio: "1/1",
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </Button>
                </motion.div>
              </Tooltip>
            </Box>

            {/* Delivery Services List */}
            {loading && isFirstLoad ? (
              <Stack spacing={2}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            ) : (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  minHeight: 300,
                }}
              >
                <Box
                  ref={listRef}
                  sx={{
                    maxHeight: "calc(100vh - 400px)",
                    minHeight: 200,
                    overflow: "auto",
                    pr: 1,
                  }}
                >
                  {/* Empty state when no services match the search */}
                  {filteredDeliveryServices.length === 0 && (
                    <Fade in={true}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 6,
                          px: 2,
                        }}
                      >
                        {searchTerm ? (
                          <>
                            <SearchIcon
                              sx={{
                                fontSize: 48,
                                color: alpha(theme.palette.text.secondary, 0.3),
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant="h6"
                              color="textSecondary"
                              align="center"
                              gutterBottom
                            >
                              Nenhum resultado encontrado
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              align="center"
                              sx={{ mb: 3, maxWidth: 400 }}
                            >
                              Não encontramos serviços correspondentes a "
                              {searchTerm}"
                            </Typography>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant="outlined"
                                onClick={handleClearSearch}
                                sx={{ borderRadius: 2 }}
                              >
                                Limpar Busca
                              </Button>
                            </motion.div>
                          </>
                        ) : (
                          <>
                            <LocalShippingIcon
                              sx={{
                                fontSize: 48,
                                color: alpha(theme.palette.text.secondary, 0.3),
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant="h6"
                              color="textSecondary"
                              align="center"
                              gutterBottom
                            >
                              Nenhum serviço de entrega cadastrado
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              align="center"
                              sx={{ mb: 3, maxWidth: 400 }}
                            >
                              Cadastre seu primeiro serviço de entrega para
                              começar a gerenciar suas entregas
                            </Typography>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={openCreateServiceModal}
                                sx={{ borderRadius: 2 }}
                              >
                                Adicionar Serviço de Entrega
                              </Button>
                            </motion.div>
                          </>
                        )}
                      </Box>
                    </Fade>
                  )}

                  {/* Delivery services list with direct rendering */}
                  {filteredDeliveryServices.length > 0 && (
                    <Box sx={{ opacity: 1 }}>
                      {/* This key forces a complete re-render when data changes */}
                      <div
                        key={`delivery-service-list-${forceUpdate}`}
                        style={{ opacity: 1 }}
                      >
                        {renderDeliveryServiceRows()}

                        {/* Load more button */}
                        {deliveryServices.length > 0 &&
                          showButtonMore &&
                          !searchTerm && (
                            <Box sx={{ mt: 2, opacity: 1 }}>
                              <Button
                                fullWidth
                                startIcon={
                                  loadingMore ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )
                                }
                                onClick={() =>
                                  loadDeliveryServices(false, null)
                                }
                                disabled={loadingMore}
                                sx={{
                                  py: 1.2,
                                  borderRadius: 2,
                                  color: theme.palette.text.secondary,
                                  bgcolor: theme.palette.grey[50],
                                  border: `1px solid ${theme.palette.grey[200]}`,
                                  "&:hover": {
                                    bgcolor: theme.palette.grey[100],
                                    borderColor: theme.palette.grey[300],
                                  },
                                }}
                              >
                                {loadingMore ? "Carregando..." : "Mostrar mais"}
                              </Button>
                            </Box>
                          )}
                      </div>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Service count badge */}
            {filteredDeliveryServices.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Chip
                  label={`${filteredDeliveryServices.length} ${
                    filteredDeliveryServices.length === 1
                      ? "serviço"
                      : "serviços"
                  }`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </ModalContent>
      </AnimatedModal>

      {showCreateEditServiceModal && (
        <DeliveryServiceCreateEdit
          id={serviceToEditId}
          show={showCreateEditServiceModal}
          closeModal={closeCreateEditServiceModal}
          updateDeliveryServices={() => loadDeliveryServices(true)}
        />
      )}

      <RemoveModal
        title="Remover Serviço de Entrega"
        toggleModal={toggleDeleteModal}
        onRemove={handleDeleteService}
        show={showRemoveModal}
        message="Tem certeza que deseja remover este serviço de entrega?"
      />
    </>
  );
}
